"use client";

import React from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
interface ProjectItem {
  id: number;
  title: string;
  category: string;
  neighborhood: string;
  description: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const projects: ProjectItem[] = [{
  id: 1,
  title: "Craftsman Kitchen Expansion",
  category: "Kitchen",
  neighborhood: "Oakhurst",
  description: "Custom inset white oak cabinetry, waterfall quartz island, and architectural skylights.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_oakhurst_kitchen.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_oakhurst_kitchen.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_oakhurst_kitchen.webp-wvc-sizes",
  imageAlt: "Bright open kitchen with natural wood island and quartz countertops in Oakhurst Decatur"
}, {
  id: 2,
  title: "Spa Primary Suite Retreat",
  category: "Master Bath",
  neighborhood: "Winnona Park",
  description: "Freestanding soaking tub, zero-threshold curbless shower, and warm copper brass fixtures.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_winnona_bathroom.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_winnona_bathroom.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_winnona_bathroom.png-wvc-sizes",
  imageAlt: "Spa primary bathroom with brass fixtures and freestanding soaking tub in Winnona Park"
}, {
  id: 3,
  title: "Historic Two-Story Rear Addition",
  category: "Addition",
  neighborhood: "Great Lakes",
  description: "Expanded family living area with matching historic exterior brickwork and vaulted ceilings.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_great_lakes_addition.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_great_lakes_addition.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_great_lakes_addition.png-wvc-sizes",
  imageAlt: "Two-story residential craftsman home addition in Great Lakes Decatur"
}, {
  id: 4,
  title: "Full 1920s Bungalow Restoration",
  category: "Whole-Home",
  neighborhood: "Downtown Decatur",
  description: "Complete interior layout reconfiguration with preserved original heart pine flooring.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_downtown_bungalow.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_downtown_bungalow.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_downtown_bungalow.png-wvc-sizes",
  imageAlt: "Restored historic bungalow living room with preserved wood floors in Downtown Decatur"
}, {
  id: 5,
  title: "Daylight Terrace Level Suite",
  category: "Basement",
  neighborhood: "Clairemont Historic District",
  description: "Finished entertainment lounge featuring custom wet bar, wine cellar, and guest suite.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_clairemont_basement.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_clairemont_basement.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_clairemont_basement.png-wvc-sizes",
  imageAlt: "Finished daylight basement entertainment room with wet bar and recessed lighting"
}, {
  id: 6,
  title: "Covered Cedar Porch & Deck",
  category: "Outdoor Living",
  neighborhood: "Lenox Place",
  description: "Integrated outdoor stone fireplace, tongue-and-groove ceiling, and perimeter dining deck.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_lenox_deck.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_lenox_deck.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_lenox_deck.png-wvc-sizes",
  imageAlt: "Covered cedar deck with outdoor stone fireplace and wooden pergola in Lenox Place"
}, {
  id: 7,
  title: "Modern European Scullery Kitchen",
  category: "Kitchen",
  neighborhood: "Winnona Park",
  description: "Integrated panel appliances, matte porcelain slabs, and dedicated butler prep pantry.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_winnona_modern_kitchen.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_winnona_modern_kitchen.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_winnona_modern_kitchen.png-wvc-sizes",
  imageAlt: "Modern kitchen with custom cabinetry and hidden walk-in pantry in Winnona Park"
}, {
  id: 8,
  title: "Vintage Tile & Vanity Overhaul",
  category: "Bathroom",
  neighborhood: "Oakhurst",
  description: "Custom double white oak vanity paired with zellige ceramic tile and reeded glass.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_oakhurst_vanity.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_oakhurst_vanity.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_oakhurst_vanity.png-wvc-sizes",
  imageAlt: "Warm oak double bathroom vanity with zellige tiles in Oakhurst renovation"
}, {
  id: 9,
  title: "Attic Suite & Dormer Conversion",
  category: "Whole-Home",
  neighborhood: "Great Lakes",
  description: "Second-floor expansion providing two quiet children bedrooms, bathroom, and flex study.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_great_lakes_attic.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_great_lakes_attic.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/gallery_great_lakes_attic.png-wvc-sizes",
  imageAlt: "Craftsman attic conversion bedroom with architectural dormer windows and built-in shelving"
}];
export default function Gallery() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="project-gallery" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            Completed Renovations
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Renovations crafted for Decatur homes.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Explore recently completed kitchens, additions, and full living transformations across our neighboring communities.
          </p>
        </div>

        {/* 3-Column Projects Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {projects.map((project, index) => <motion.div key={project.id} data-index={index} variants={itemVariants} whileHover={shouldReduceMotion ? undefined : {
          y: -4
        }} className="group flex flex-col rounded-xl">
              <Card className="flex flex-col h-full bg-card/90 backdrop-blur-md border border-border rounded-xl shadow-xs overflow-hidden transition-[border-color,box-shadow] duration-300 hover:border-border hover:shadow-md py-0">
                {/* Project Image */}
                <div className="p-4 pb-0">
                  <div className="relative overflow-hidden rounded-md border border-border/80 bg-muted">
                    <AspectRatio ratio={16 / 10}>
                      <img src={project.imageSrc} data-wvc-srcset={project.imageSrcset} data-wvc-sizes={project.imageSizes} alt={project.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </AspectRatio>
                  </div>
                </div>

                {/* Card Body */}
                <CardContent className="p-6 flex flex-col flex-1 justify-between gap-4">
                  <div className="space-y-3">
                    {/* Tags */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="rounded-sm bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground uppercase tracking-wide border border-border">
                        {project.category}
                      </Badge>
                      <div className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                        <MapPin className="size-3 text-muted-foreground" />
                        <span>{project.neighborhood}</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-card-foreground group-hover:text-foreground transition-colors">
                      {project.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  {/* Tertiary Action Link */}
                  <div className="pt-2 border-t border-border">
                    <Link to="/ContactPage" className="inline-flex items-center gap-2 text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-3 hover:text-primary">
                      <span>Plan a similar project</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Context Callout */}
        <div className="mt-14 sm:mt-16 text-center border-t border-border pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Serving Decatur neighborhoods including Oakhurst, Winnona Park, Great Lakes, and Downtown Decatur.
          </p>
          <Link to="/ProcessPlanningPage" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary transition-colors">
            <span>See our planning and cost guidance</span>
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </div>
    </section>;
}